// src/components/contacts/ContactList.jsx
import React, { useState } from 'react';
import { 
  FiEdit, FiTrash2, FiMail, FiMessageSquare, FiExternalLink,
  FiUser, FiHome, FiPhone, FiLinkedin, FiSmartphone
} from 'react-icons/fi';

const ContactList = ({ 
  contacts, 
  selectedContacts, 
  onSelectionChange, 
  onEditContact, 
  onDeleteContact, 
  loading 
}) => {
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleSelectAll = () => {
    if (selectedContacts.length === contacts.length) {
      onSelectionChange([]);
    } else {
      onSelectionChange(contacts.map(contact => contact.id));
    }
  };

  const handleSelectContact = (contactId) => {
    if (selectedContacts.includes(contactId)) {
      onSelectionChange(selectedContacts.filter(id => id !== contactId));
    } else {
      onSelectionChange([...selectedContacts, contactId]);
    }
  };

  const sortedContacts = [...contacts].sort((a, b) => {
    const aValue = a[sortField] || '';
    const bValue = b[sortField] || '';
    
    if (sortDirection === 'asc') {
      return aValue.localeCompare(bValue, undefined, { numeric: true });
    } else {
      return bValue.localeCompare(aValue, undefined, { numeric: true });
    }
  });

  const getSortIcon = (field) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  const getTypeIcon = (type) => {
    return type === 'Person' ? <FiUser size={16} /> : <FiHome size={16} />;
  };

  const formatLinkedInUrl = (linkedin) => {
    if (!linkedin) return '';
    if (linkedin.startsWith('http')) return linkedin;
    if (linkedin.startsWith('linkedin.com')) return `https://${linkedin}`;
    if (linkedin.startsWith('/in/')) return `https://linkedin.com${linkedin}`;
    return `https://linkedin.com/in/${linkedin}`;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="animate-pulse">
          <div className="h-12 bg-gray-100"></div>
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="h-16 bg-gray-50 border-t border-gray-100"></div>
          ))}
        </div>
      </div>
    );
  }

  if (contacts.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
        <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
          <FiUser className="text-gray-400" size={24} />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">No contacts found</h3>
        <p className="text-gray-600">Start by adding your first contact or import from a file.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Table Header */}
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="grid grid-cols-12 gap-4 px-3 py-3 text-sm font-medium text-gray-600">
          <div className="col-span-1 flex items-center">
            <input
              type="checkbox"
              checked={selectedContacts.length === contacts.length && contacts.length > 0}
              onChange={handleSelectAll}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
          </div>
          
          <div 
            className="col-span-2 cursor-pointer hover:text-gray-900 flex items-center"
            onClick={() => handleSort('name')}
          >
            Name {getSortIcon('name')}
          </div>
          
          <div 
            className="col-span-1 cursor-pointer hover:text-gray-900 flex items-center"
            onClick={() => handleSort('type')}
          >
            Type {getSortIcon('type')}
          </div>
          
          <div 
            className="col-span-2 cursor-pointer hover:text-gray-900 flex items-center"
            onClick={() => handleSort('organisation')}
          >
            Organisation {getSortIcon('organisation')}
          </div>
          
          <div 
            className="col-span-1 cursor-pointer hover:text-gray-900 flex items-center"
            onClick={() => handleSort('industry')}
          >
            Industry {getSortIcon('industry')}
          </div>
          
          <div className="col-span-1 text-center">
            <FiMail size={16} className="mx-auto" title="Email" />
          </div>
          
          <div className="col-span-1 text-center">
            <FiPhone size={16} className="mx-auto" title="Telephone" />
          </div>
          
          <div className="col-span-1 text-center">
            <FiSmartphone size={16} className="mx-auto" title="Mobile" />
          </div>
          
          <div className="col-span-1 text-center">
            <FiLinkedin size={16} className="mx-auto" title="LinkedIn" />
          </div>
          
          <div className="col-span-1 text-center">
            Actions
          </div>
        </div>
      </div>

      {/* Table Body */}
      <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
        {sortedContacts.map((contact) => (
          <div 
            key={contact.id} 
            className={`grid grid-cols-12 gap-4 px-3 py-4 hover:bg-gray-50 ${
              selectedContacts.includes(contact.id) ? 'bg-blue-50' : ''
            }`}
          >
            <div className="col-span-1 flex items-center">
              <input
                type="checkbox"
                checked={selectedContacts.includes(contact.id)}
                onChange={() => handleSelectContact(contact.id)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
            </div>
            
            <div className="col-span-2 flex items-center">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center mr-3">
                  {getTypeIcon(contact.type)}
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-900">
                    {contact.name || 'Unnamed Contact'}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="col-span-1 flex items-center">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                contact.type === 'Person' 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-blue-100 text-blue-800'
              }`}>
                {contact.type}
              </span>
            </div>
            
            <div className="col-span-2 flex items-center">
              <div className="text-sm text-gray-900">
                {contact.organisation || '-'}
              </div>
            </div>
            
            <div className="col-span-1 flex items-center">
              <div className="text-sm text-gray-900">
                {contact.industry || '-'}
              </div>
            </div>
            
            {/* Email - Icon Only */}
            <div className="col-span-1 flex items-center justify-center">
              {contact.email ? (
                <a 
                  href={`mailto:${contact.email}`}
                  className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                  title={`Email: ${contact.email}`}
                >
                  <FiMail size={16} />
                </a>
              ) : (
                <span className="text-gray-300">
                  <FiMail size={16} />
                </span>
              )}
            </div>
            
            {/* Telephone - Icon Only */}
            <div className="col-span-1 flex items-center justify-center">
              {contact.tel ? (
                <a 
                  href={`tel:${contact.tel}`}
                  className="p-2 text-green-600 hover:text-green-800 hover:bg-green-50 rounded-lg transition-colors"
                  title={`Tel: ${contact.tel}`}
                >
                  <FiPhone size={16} />
                </a>
              ) : (
                <span className="text-gray-300">
                  <FiPhone size={16} />
                </span>
              )}
            </div>
            
            {/* Mobile - Icon Only */}
            <div className="col-span-1 flex items-center justify-center">
              {contact.mobile ? (
                <a 
                  href={`tel:${contact.mobile}`}
                  className="p-2 text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded-lg transition-colors"
                  title={`Mobile: ${contact.mobile}`}
                >
                  <FiSmartphone size={16} />
                </a>
              ) : (
                <span className="text-gray-300">
                  <FiSmartphone size={16} />
                </span>
              )}
            </div>
            
            {/* LinkedIn - Icon Only */}
            <div className="col-span-1 flex items-center justify-center">
              {contact.linkedin ? (
                <a
                  href={formatLinkedInUrl(contact.linkedin)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 text-blue-700 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors"
                  title={`LinkedIn: ${contact.linkedin}`}
                >
                  <FiLinkedin size={16} />
                </a>
              ) : (
                <span className="text-gray-300">
                  <FiLinkedin size={16} />
                </span>
              )}
            </div>
            
            <div className="col-span-1 flex items-center justify-center space-x-1">
              <button
                onClick={() => onEditContact(contact)}
                className="p-1 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                title="Edit Contact"
              >
                <FiEdit size={14} />
              </button>
              
              <button
                onClick={() => onDeleteContact(contact.id)}
                className="p-1 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                title="Delete Contact"
              >
                <FiTrash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="bg-gray-50 px-6 py-3 border-t border-gray-200">
        <div className="flex justify-between items-center text-sm text-gray-600">
          <span>Showing {contacts.length} contacts</span>
          <span>{selectedContacts.length} selected</span>
        </div>
      </div>
    </div>
  );
};

export default ContactList;